/**
 * Host Login Wrapper Component
 *
 * This file contains the implementation of a React component called LoginWrapper that serves as a wrapper for the host login page. 
 * The component handles the logic for displaying active and previous hunts, updating user information, 
 * and rendering the host navigation panel. It also includes methods for handling image uploads 
 * and displaying bonus points. The component uses the dayjs library for date and time 
 * manipulation and makes API calls to retrieve user and hunt information.
 */

import React from 'react'
import dayjs from 'dayjs'
import { isHostLoggedIn } from '../../utils/host-auth'
import { navigate } from '../../utils/navigate'
import AddBonusPoints from './add-bonus-points'
import API from '../../utils/api'
import HostNavPanel from './nav-panel'
import SvgHamburger from '../../components/svg/hamburger'
import SvgPrint from '../../components/svg/print'
import HostContext from '../../utils/host-context'
import { groupHunts } from '../../utils/host-grouping'
import PhotoUpload from './photos/photo-upload'

export default class HostLoginWrapper extends React.Component {
  constructor(props) {
    super(props)
    this.setNavOpen = this.setNavOpen.bind(this)
    this.toggleNav = this.toggleNav.bind(this)

    this.setBonusPointsOpen = this.setBonusPointsOpen.bind(this)
    this.toggleBonusPoints = this.toggleBonusPoints.bind(this)

    this.setActiveHunt = this.setActiveHunt.bind(this)
    this.setCombinedTeams = this.setCombinedTeams.bind(this)
    this.sort_hunts = this.sort_hunts.bind(this)
    this.get_active_hunts = this.get_active_hunts.bind(this)
    this.get_hunt_groups = this.get_hunt_groups.bind(this)

    this.update = this.update.bind(this)

    this.addPointsTeam = this.addPointsTeam.bind(this)
    this.setTeamID = this.setTeamID.bind(this)

    this.uploadModalClose = this.uploadModalClose.bind(this)
    this.setUploadModalShow = this.setUploadModalShow.bind(this)
    this.handleUpload = this.handleUpload.bind(this)

    this.state = {
      navOpen: false,
      setNavOpen: this.setNavOpen,
      toggleNav: this.toggleNav,

      bonusPointsOpen: false,
      setBonusPointsOpen: this.setBonusPointsOpen,
      toggleBonusPoints: this.toggleBonusPoints,
      addPointsTeam: this.addPointsTeam,

      user: {},
      hunts: [],
      loggedIn: false,
      loaded: false,
      updating: false,

      active_hunts: [],
      previous_hunts: [],

      active_hunt: {},
      combined_teams: [],
      setActiveHunt: this.setActiveHunt,
      setCombinedTeams: this.setCombinedTeams,
      get_active_hunts: this.get_active_hunts,
      get_hunt_groups: this.get_hunt_groups,

      team_id: ``,
      setTeamID: this.setTeamID,

      uploadImage: ``,
      uploadModalShow: false,
      uploadModalClose: this.uploadModalClose,
      setUploadModalShow: this.setUploadModalShow,
      handleUpload: this.handleUpload,
      uploadCallback: () => {},
    }
  }

  setNavOpen(navOpen) {
    //console.log(`setting navOpen to ${navOpen}`)
    return new Promise(resolve => this.setState({ navOpen: navOpen }, () => resolve()))
  }

  toggleNav() {
    //console.log('toggling nav')
    return new Promise(resolve => this.setState({ navOpen: !this.state.navOpen }, () => resolve()))
  }

  setBonusPointsOpen(pointsOpen) {
    //console.log(`setting bonusPointsOpen to ${pointsOpen}`)
    return new Promise(resolve => this.setState({ bonusPointsOpen: pointsOpen }, () => resolve()))
  }

  toggleBonusPoints() {
    //console.log('toggling bonusPoints')
    return new Promise(resolve => this.setState({ bonusPointsOpen: !this.state.bonusPointsOpen }, () => resolve()))
  }

  setActiveHunt(hunt) {
    return new Promise(resolve => this.setState({ active_hunt: hunt }, () => resolve()))
  }

  setCombinedTeams(teams) {
    return new Promise(resolve => this.setState({ combined_teams: teams || [] }, () => resolve()))
  }

  addPointsTeam(team_id) {
    this.setState({
      team_id: team_id,
      bonusPointsOpen: true,
    })
  }

  setTeamID(team_id) {
    this.setState({
      team_id: team_id,
    })
  }

  sort_hunts() {
    const today = dayjs()
    let active_hunts = []
    let previous_hunts = []
    this.state.hunts.map(hunt => {
      if (hunt.event_date) {
        let hunt_date = dayjs(hunt.event_date)
        if (hunt_date.isBefore(today, `day`)) {
          previous_hunts.push(hunt)
        } else {
          active_hunts.push(hunt)
        }
      } else {
        if (hunt.start) {
          let hunt_time = dayjs(hunt.start)
          let hunt_end_time = null
          if (hunt.end) {
            hunt_end_time = dayjs(hunt.end)
          }
          if (hunt_end_time && hunt_end_time.isBefore(today, `day`)) {
            previous_hunts.push(hunt)
          } else {
            active_hunts.push(hunt)
          }
        }
      }
    })
    this.setState({
      active_hunts: active_hunts,
      previous_hunts: previous_hunts,
    })
  }

  get_active_hunts() {
    const { hunts } = this.state
    const today = dayjs()
    let active_hunts = []
    hunts.map(hunt => {
      if (hunt.event_date) {
        let hunt_date = dayjs(hunt.event_date)
        if (!hunt_date.isBefore(today, `day`)) {
          active_hunts.push(hunt)
        } else {
          let hunt_end_time = null
          if (hunt.end) {
            hunt_end_time = dayjs(hunt.end)
          }
          if (hunt_end_time && !hunt_end_time.isBefore(today, `day`)) {
            active_hunts.push(hunt)
          }
        }
      } else {
        if (hunt.start) {
          let hunt_time = dayjs(hunt.start)
          let hunt_end_time = null
          if (hunt.end) {
            hunt_end_time = dayjs(hunt.end)
          }
          if (hunt_end_time && !hunt_end_time.isBefore(today, `day`)) {
            active_hunts.push(hunt)
          }
        }
      }
    })
    return active_hunts
  }

  get_hunt_groups() {
    return groupHunts(this.get_active_hunts())
  }

  update() {
    const { updating } = this.state
    if (!updating) {
      this.setState({ updating: true })
      API.get(`/current_user/`, {
        headers: {
          Authorization: `JWT ${localStorage.getItem(`token`)}`,
        },
        timeout: 15000,
      })
        .then(res => {
          let json = res.data
          this.setState(
            {
              user: json.user,
              hunts: json.hunts,
              loggedIn: true,
              loaded: true,
              updating: false,
            },
            () => {
              // this.sort_hunts()
            }
          )
        })
        .catch(error => {
          console.log(error)
          // A dropped request (bad museum signal) used to leave `updating`
          // stuck, so the host list never retried and sat on "Loading...".
          this.setState({ updating: false })
        })
    }
  }

  uploadModalClose() {
    this.setState({ uploadModalShow: false, uploadImage: `` })
  }

  setUploadModalShow(value, image, uploadCallback = () => {}) {
    this.setState({ uploadModalShow: value, uploadImage: image, uploadCallback: uploadCallback })
  }

  handleUpload(response) {
    this.state.uploadCallback(response)
    this.uploadModalClose()
  }

  componentDidMount() {
    isHostLoggedIn().then(loggedIn => {
      console.log(`logged in: ${loggedIn}`)
      if (loggedIn) {
        this.update()
        this.interval = setInterval(this.update, 5000)
      } else {
        navigate(`/host/login/`)
      }
    })
  }

  componentWillUnmount() {
    clearInterval(this.interval)
  }

  render() {
    const { navOpen, loggedIn, bonusPointsOpen, active_hunt, combined_teams, user } = this.state
    const { children } = this.props
    const navClass = navOpen ? `nav-is-active` : ``
    const bonusPointsClass = bonusPointsOpen ? `nav-points-is-active` : ``

    let team_options = [{ name: ``, value: `` }]
    const source_teams =
      combined_teams && combined_teams.length > 0
        ? combined_teams
        : active_hunt.teams !== undefined
        ? active_hunt.teams
        : []
    source_teams.map(team => {
      if (!team.permanent) {
        const label = team.team_name ? `#${team.number} - ${team.access_code} (${team.team_name})` : `#${team.number} - ${team.access_code}`
        const game_suffix = team.game_label ? ` — ${team.game_label}` : ``
        team_options.push({ name: `${label}${game_suffix}`, value: team.id })
      }
    })
    return (
      <HostContext.Provider value={this.state}>
        <div className={`page-wrap ${navClass} ${bonusPointsClass}`}>
          <header className="site-header navbar navbar-expand navbar-dark">
            <button className={`btn btn-link menu-toggle`} onClick={this.toggleNav} title="Menu">
              <SvgHamburger />
            </button>
            {active_hunt.id && (
              <a
                className="btn btn-link ml-auto"
                href={`/host/print/#${active_hunt.id}`}
                target="_blank"
                rel="noreferrer">
                <SvgPrint />
              </a>
            )}
          </header>
          <div className={`container-fluid px-0`}>{children}</div>
          <div className="site-menu site-menu-nav" role="navigation">
            <nav>
              <div className={`px-5 mt-2`}>
                <button
                  className={`px-0 py-4 btn btn-link menu-toggle ${navClass}`}
                  onClick={this.toggleNav}
                  title="Close">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
                    <g fill="none" fillRule="evenodd" stroke="#273A41" strokeLinecap="round">
                      <path d="M.5.5l16.867 16.867M17.5.5L.633 17.367" />
                    </g>
                  </svg>
                </button>
              </div>
              <HostNavPanel />
            </nav>
          </div>
          {active_hunt.id && (
            <AddBonusPoints team_options={team_options} user_id={user.id !== undefined ? user.id : ``} />
          )}
        </div>
      </HostContext.Provider>
    )
  }
}
